/**
 * Type extensions for Supabase tables, views and RPC functions that are not
 * included in the auto-generated types.ts (generated from public schema only).
 *
 * When supabase gen types is re-run these should either be absorbed into
 * types.ts (preferred) or kept here if the objects are created outside the
 * migration flow.
 */

import type { SupabaseClient } from "@supabase/supabase-js";
import type { Database } from "./types";
import type {
  SessionStateEntry,
  SessionSummary,
  AnalysisUser,
  AnalysisFilter,
  FunnelStep,
  StepTransitionTime,
} from "@/types/sessionState";
import type { ProductsResponse } from "@/hooks/createProductsQueryFn";
import { supabase as baseSupabase } from "./client";

// ---------------------------------------------------------------------------
// Row shapes
// ---------------------------------------------------------------------------

type CacheRow = {
  key: string;
  json_payload: ProductsResponse;
  expires_on: string;
};

type UspRow = {
  product_id: string;
  use_case: string;
  usp: unknown;
};

type OverviewStatsRow = {
  total_sessions: number;
  completed_sessions: number;
  conversion_rate: number;
};

type JourneyPathRow = {
  path: string;
  count: number;
};

// get_analysis_marketing_sources aggregates server-side; the numeric columns
// arrive as strings from PostgREST for BIGINT/NUMERIC, hence the Number() casts
// at the call site.
type MarketingSourceRow = {
  source: string;
  medium: string | null;
  campaign: string | null;
  sessions: number;
  conversions: number;
  conversion_rate: number;
};

export type BookingAttemptRow = {
  email: string;
  name: string | null;
  market: string | null;
  test_mode: boolean | null;
  attempts: number;
  first_attempt_at: string;
  last_attempt_at: string;
  is_stripe: boolean;
  form_agreed: boolean;
  booked: boolean;
  booked_within_15min: boolean;
};

export type BookingTimelineRow = {
  event_time: string;
  event_type: "attempt" | "stripe_pending" | "stripe_fulfilled" | "booking";
  detail: Record<string, unknown>;
};

// ---------------------------------------------------------------------------
// Extended Database type
// ---------------------------------------------------------------------------

// `Relationships` is required by postgrest-js's GenericTable / GenericView.
// Omitting it makes ExtendedDatabase["public"] fail `extends GenericSchema`,
// which silently collapses the client's Schema to `never` — every .from() and
// .rpc() on typedSupabase then errors with "not assignable to parameter of type
// 'never'". These objects have no declared FK relationships, hence [].
type ExtendedTables = Database["public"]["Tables"] & {
  session_state_entries: {
    Row: SessionStateEntry;
    Insert: Omit<SessionStateEntry, "id">;
    Update: Partial<SessionStateEntry>;
    Relationships: [];
  };
  session_summaries: {
    Row: SessionSummary;
    Insert: Partial<SessionSummary>;
    Update: Partial<SessionSummary>;
    Relationships: [];
  };
  analysis_users: {
    Row: AnalysisUser;
    Insert: Omit<AnalysisUser, "created_at">;
    Update: Partial<AnalysisUser>;
    Relationships: [];
  };
  analysis_filters: {
    Row: AnalysisFilter;
    // is_active is `BOOLEAN DEFAULT true` in the migration, so the column is
    // optional on insert — createAnalysisFilter never sends it. The previous
    // Omit<> made it required, which only surfaced once the schema stopped
    // collapsing to `never`.
    Insert: Omit<AnalysisFilter, "id" | "created_at" | "is_active"> & {
      is_active?: boolean;
    };
    Update: Partial<AnalysisFilter>;
    Relationships: [];
  };
  cache: {
    Row: CacheRow;
    Insert: CacheRow;
    Update: Partial<CacheRow>;
    Relationships: [];
  };
  usp: {
    Row: UspRow;
    Insert: UspRow;
    Update: Partial<UspRow>;
    Relationships: [];
  };
};

type ExtendedViews = Database["public"]["Views"] & {
  filtered_session_summaries: {
    Row: SessionSummary;
    Relationships: [];
  };
  filtered_session_state_entries: {
    Row: SessionStateEntry;
    Relationships: [];
  };
};

type ExtendedFunctions = Database["public"]["Functions"] & {
  get_latest_session_state: {
    Args: { p_session_id: string };
    Returns: SessionStateEntry[];
  };
  get_funnel_data: {
    Args: { date_from: string };
    Returns: FunnelStep[];
  };
  get_analysis_overview_stats: {
    Args: { p_date_from: string; p_date_to: string; p_date_col: string };
    Returns: OverviewStatsRow[];
  };
  get_analysis_funnel: {
    Args: { p_date_from: string; p_date_to: string; p_date_col: string };
    Returns: FunnelStep[];
  };
  get_analysis_step_transitions: {
    Args: { p_date_from: string; p_date_to: string };
    Returns: StepTransitionTime[];
  };
  get_analysis_journey_paths: {
    Args: { p_date_from: string; p_date_to: string };
    Returns: JourneyPathRow[];
  };
  get_analysis_recent_events: {
    Args: { p_limit: number };
    Returns: SessionStateEntry[];
  };
  get_analysis_marketing_sources: {
    Args: { p_date_from: string; p_date_to: string; p_date_col: string };
    Returns: MarketingSourceRow[];
  };
  get_booking_attempts: {
    Args: Record<string, never>;
    Returns: BookingAttemptRow[];
  };
  get_booking_timeline: {
    Args: { p_email: string };
    Returns: BookingTimelineRow[];
  };
};

type ExtendedDatabase = Omit<Database, "public"> & {
  public: Omit<Database["public"], "Tables" | "Views" | "Functions"> & {
    Tables: ExtendedTables;
    Views: ExtendedViews;
    Functions: ExtendedFunctions;
  };
};

// ---------------------------------------------------------------------------
// Typed client — same underlying instance, just re-typed
// ---------------------------------------------------------------------------

export const typedSupabase =
  baseSupabase as unknown as SupabaseClient<ExtendedDatabase>;
